import { useCallback } from 'react';
import { useAIEventsInstrumentation } from '@atlassian/ai-analytics';
import {
	CORE_PROJECT,
	SOFTWARE_PROJECT,
} from '@atlassian/jira-common-constants/src/project-types.tsx';
import { useIssueKey } from '@atlassian/jira-issue-context-service/src/main.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import {
	useProjectKey,
	useProjectType,
} from '@atlassian/jira-project-context-service/src/main.tsx';
import type { SuggestedCommentReplyAgentCommentType } from '@atlassian/jira-ai-assistance-service-client/src/services/ai-features-client/suggested-comment-reply-agent/types.tsx';
import { hasSuggestedComment, useAIComments } from '../../services/use-ai-comments/index.tsx';
import { useAICommentsStates } from '../use-ai-comments-context/index.tsx';

export const useAICommentsInteraction = () => {
	const { fetchSuggestedComments } = useAIComments();
	const issueKey = useIssueKey();
	const projectKey = useProjectKey(issueKey);
	const projectType = useProjectType(projectKey);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const [
		{ availableCommentTypes, suggestedComments, isLoading },
		{ setSuggestedComments, setCurrentCommentType, setShowGeneratedComment, setIsLoading },
	] = useAICommentsStates();

	const { trackAIInteractionInit, trackAIResultError } = useAIEventsInstrumentation();

	const onFail = useCallback(
		(type: SuggestedCommentReplyAgentCommentType) => (error: Error) => {
			setShowGeneratedComment(false);

			trackAIResultError(
				{
					aiErrorMessage: error?.message,
				},
				{
					attributes: {
						aiExperienceName: type,
					},
				},
			);
		},
		[setShowGeneratedComment, trackAIResultError],
	);

	const handleCommentButtonClick = useCallback(
		(type: SuggestedCommentReplyAgentCommentType): void => {
			trackAIInteractionInit({
				attributes: {
					aiExperienceName: type,
				},
			});
			setCurrentCommentType(type);
			setShowGeneratedComment(true);
			fireUIAnalytics(createAnalyticsEvent({}), 'button clicked', 'aiCannedComment', {
				commentType: type,
			});

			if (
				!hasSuggestedComment(type, suggestedComments) &&
				!isLoading &&
				(projectType === CORE_PROJECT || projectType === SOFTWARE_PROJECT)
			) {
				setIsLoading(true);
				fetchSuggestedComments({
					issueKey,
					projectType,
					selectedType: type,
					commentTypes: availableCommentTypes,
					onFail: onFail(type),
				}).then((data) => {
					setSuggestedComments(data);
					setIsLoading(false);
				});
			}
		},
		[
			availableCommentTypes,
			createAnalyticsEvent,
			fetchSuggestedComments,
			isLoading,
			issueKey,
			onFail,
			projectType,
			setCurrentCommentType,
			setIsLoading,
			setShowGeneratedComment,
			setSuggestedComments,
			suggestedComments,
			trackAIInteractionInit,
		],
	);

	return {
		handleCommentButtonClick,
	};
};
